import { graphql } from 'react-relay';

import type { Option } from '@atlaskit/editor-common/extensions/field-definitions';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import type { LegacyMacroManifest } from '../extensionTypes.ts';

import type {
	spacekeyResolverQuery$data,
	spacekeyResolverQuery$variables,
} from './__generated__/spacekeyResolverQuery.graphql.ts';
import { toArray } from './utils.ts';

// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- file name contains hyphens, incompatible with GraphQL naming
const spacekeyResolverQuery = graphql`
	query spacekeyResolverQuery($cql: String!) {
		search: confluence_search(cql: $cql, first: 25) {
			edges {
				node {
					entityType
					title
					space {
						key
						icon {
							path
						}
					}
				}
			}
		}
	}
`;

const search = async (cql: string) => {
	const result = await queryInterop<spacekeyResolverQuery$data, spacekeyResolverQuery$variables>({
		query: spacekeyResolverQuery,
		variables: { cql },
	});

	return (result?.data?.search?.edges ?? []).map((edge) => ({
		label: edge?.node?.title ?? '',
		value: edge?.node?.space?.key ?? '',
		icon: `/wiki${edge?.node?.space?.icon?.path ?? ''}`,
	}));
};

const quoteString = (str: string) => `"${str}"`;

export const spacesInCqlClause = (spaces: string[]) =>
	`space.key IN (${spaces.map(quoteString).join(',')})`;

const searchByTitleOrSpaceKey = (searchTerm: string, selectedValues: string[] = []) =>
	search(
		`type = space AND (space.title ~ "${searchTerm}*" OR ${spacesInCqlClause(selectedValues.concat(searchTerm))})`,
	);

export const buildResolver =
	(supportsCurrentSpace: boolean) =>
	async (searchTerm?: string, selectedValue?: string | string[]): Promise<Option[]> => {
		const selectedValues = toArray(selectedValue);
		const searchers: Array<Promise<Option[]>> = [];

		if (searchTerm) {
			searchers.push(searchByTitleOrSpaceKey(searchTerm, selectedValues));
		} else {
			if (supportsCurrentSpace) {
				searchers.push(Promise.resolve([{ label: 'Current space', value: 'currentSpace()' }]));
			}

			const conditionalCql = selectedValues.length
				? ` OR ${spacesInCqlClause(selectedValues)}`
				: '';

			searchers.push(
				search(`type = space AND (space in recentlyViewedSpaces(4) ${conditionalCql})`),
			);
		}

		return (await Promise.all(searchers)).flat();
	};

export const spacekeyResolver = (
	macro: LegacyMacroManifest,
): ((searchTerm?: string, selectedValue?: string | string[]) => Promise<Option[]>) => {
	// create-from-template doesn't support CQL-style
	// `currentSpace()` values, so don't show that menu option
	//
	// likewise for pagetree
	if (['create-from-template', 'pagetree'].indexOf(macro.macroName) > -1) {
		return buildResolver(false);
	} else {
		return buildResolver(true);
	}
};
